import { AppButton } from "@/components/teacher/AppButton";
import { TeacherPage } from "@/components/teacher/TeacherPage";
import { ClassLessonTable } from "@/components/teacher/selfStudy/ClassLessonTable";
import {
  ClassLessonTreeCard,
  type LessonGroup,
} from "@/components/teacher/selfStudy/ClassLessonTreeCard";
import { ClassroomLessonTreeDocument } from "@/generated/graphql";
import { useT } from "@/i18n";
import { useQuery } from "@apollo/client/react";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useClassroomName } from "./useClassroomData";

/** What the class can practise, and how each student is getting on with it, lesson by lesson. */
export function ClassroomSelfStudyPage({ classroomId }: { classroomId: string }) {
  const navigate = useNavigate();
  const t = useT();
  const classroomName = useClassroomName("teacher", classroomId);

  // The folders walked into, outermost first. Empty reads the root level.
  const [path, setPath] = useState<{ projectId: string; name: string }[]>([]);
  const [openGroup, setOpenGroup] = useState<LessonGroup | null>(null);

  // One level at a time, as the student report does: opening a folder is what fetches it.
  const treeQuery = useQuery(ClassroomLessonTreeDocument, {
    variables: { classroomId, parentId: path[path.length - 1]?.projectId ?? null },
  });

  return (
    <TeacherPage
      title={classroomName ?? t["selfStudy.title"]}
      subtitle={t["selfStudy.classroomCaption"]}
      actions={
        <AppButton tone="glass" onClick={() => navigate({ to: "/teacher/self-study-record" })}>
          {t["common.back"]}
        </AppButton>
      }
    >
      <ClassLessonTreeCard
        level={treeQuery.data?.classroomLessonTree}
        path={path}
        onEnterProject={(project) => setPath((prev) => [...prev, project])}
        onBack={() => setPath((prev) => prev.slice(0, -1))}
        onOpenGroup={setOpenGroup}
        loading={treeQuery.loading && !treeQuery.data}
        failed={!!treeQuery.error && !treeQuery.data}
      />

      {openGroup && (
        <ClassLessonTable
          classroomId={classroomId}
          group={openGroup}
          onClose={() => setOpenGroup(null)}
        />
      )}
    </TeacherPage>
  );
}
